import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Input, Label, Switch, Separator } from '@yaradigitallabs/ahua-react';
import { SoftTagList } from './list';
import Color from '../../../../public/GlobalTokens/Colors';

import { useNotification } from '@/components/Notification/useNotification';
import type {
  AssetSoftTag,
  Material,
  MaterialComponent,
  MaterialRouting,
  SoftTag,
  TimeSeriesSoftTag,
} from '../utils/match-maker.types';
import { SelectedSoftTagList } from './selectedList';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  createTagMapping,
  getTimeSeriesSoftTags,
  removeTagMapping,
  updateTagMapping,
} from '../store/material-soft-tag.thunk';
import {
  selectTimeSeriesSoftTags,
  selectTimeSeriesSoftTagLoading,
  selectTagMappingLoading,
} from '../store/material-soft-tag.selectors';
import { clearTimeSeriesSoftTags } from '../store/material-soft-tag.slice';
import Drawer from '@/components/Drawer';
import {
  getItemDescription,
  getItemNumber,
  getItemTypeFromSelectedItem,
  getSelectedItemFamily,
  mapMaterialTagToSoftTag,
  mapSoftTagToTagMappingRequest,
  mapAssetToSoftTag,
} from '../utils/match-maker.helpers';
import { MAX_SELECTED_SOFT_TAGS } from '../utils/match-maker.constants';
import { useBlocker, useNavigate } from 'react-router-dom';
import { SoftTagDetailCard } from '../components/SoftTagDetailCard';
import { LeaveWithoutConfirmDialog } from './leave-without-confirm-dialog';

interface SoftTagListWrapperProps {
  isLoading?: boolean;
  selectedItem?: Material | MaterialComponent | MaterialRouting;
}

const getErrorMessage = (reason: unknown): string => {
  if (typeof reason === 'string') {
    return reason;
  }

  if (reason instanceof Error) {
    return reason.message;
  }

  if (
    typeof reason === 'object' &&
    reason !== null &&
    'message' in reason &&
    typeof (reason as { message?: unknown }).message === 'string'
  ) {
    return (reason as { message: string }).message;
  }

  if (
    typeof reason === 'object' &&
    reason !== null &&
    'error' in reason &&
    typeof (reason as { error?: unknown }).error === 'string'
  ) {
    return (reason as { error: string }).error;
  }

  return 'Unknown error';
};

export const SoftTagListWrapper = ({
  isLoading = false,
  selectedItem,
}: SoftTagListWrapperProps) => {
  const showNotification = useNotification();
  const dispatch = useAppDispatch();
  const timeSeriesSoftTags = useAppSelector(selectTimeSeriesSoftTags);
  const timeSeriesSoftTagLoading = useAppSelector(
    selectTimeSeriesSoftTagLoading
  );
  const tagMappingLoading = useAppSelector(selectTagMappingLoading);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewOnlyValues, setViewOnlyValues] = useState(true);
  const [selectedSoftTags, setSelectedSoftTags] = useState<SoftTag[]>([]);
  const [softTagDetail, setSoftTagDetail] = useState<SoftTag | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const navigate = useNavigate();

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return isDirty && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setIsLeaveDialogOpen(true);
    }
  }, [blocker.state]);

  const handleKeepSelecting = useCallback(() => {
    setIsLeaveDialogOpen(false);
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
  }, [blocker]);

  const handleLeavePage = useCallback(() => {
    setIsLeaveDialogOpen(false);
    if (blocker.state === 'blocked') {
      blocker.proceed();
    }
  }, [blocker]);

  const handleSoftTagDetailClose = () => {
    setSoftTagDetail(null);
  };

  const selectedItemTags = useMemo<SoftTag[]>(() => {
    return (selectedItem?.tags || [])
      .slice()
      .sort((a, b) => {
        const aPriority = a.priority ?? Number.MAX_SAFE_INTEGER;
        const bPriority = b.priority ?? Number.MAX_SAFE_INTEGER;
        return aPriority - bPriority;
      })
      .map(tag => mapMaterialTagToSoftTag(tag));
  }, [selectedItem]);

  useEffect(() => {
    setSelectedSoftTags(selectedItemTags);
  }, [selectedItemTags]);

  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    dispatch(clearTimeSeriesSoftTags());

    if (trimmedQuery.length <= 4) {
      return;
    }

    const debounceTimer = setTimeout(() => {
      dispatch(getTimeSeriesSoftTags({ tagNames: trimmedQuery }));
    }, 400);

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [dispatch, searchQuery]);

  const mappedSoftTags = useMemo<SoftTag[]>(() => {
    if (searchQuery.trim().length <= 4) {
      return [];
    }

    const softTags = timeSeriesSoftTags?.assets || [];
    const mapped = softTags.map(softTag => mapAssetToSoftTag(softTag));
    if (!viewOnlyValues) {
      return mapped;
    }

    return mapped.filter(item => item.type?.toLowerCase() === 'value');
  }, [timeSeriesSoftTags, searchQuery, viewOnlyValues]);

  const handleToggleSelect = (item: SoftTag, checked: boolean) => {
    setSelectedSoftTags(prev => {
      const itemKey = item.timeseries_id;
      const exists = prev.some(selected => selected.timeseries_id === itemKey);

      if (checked) {
        if (exists) {
          return prev;
        }

        if (prev.length >= MAX_SELECTED_SOFT_TAGS) {
          showNotification(
            `You can select up to ${MAX_SELECTED_SOFT_TAGS} soft tags.`,
            'destructive'
          );
          return prev;
        }

        return [
          ...prev,
          {
            ...item,
            priority: prev.length + 1,
          },
        ];
      }
      return prev.filter(selected => selected.timeseries_id !== itemKey);
    });
    setIsDirty(true);
  };

  const selectedIds = selectedSoftTags.map(item => item.timeseries_id);

  const handleRemoveSelectedSoftTag = (removedItem: SoftTag) => {
    setSelectedSoftTags(prev =>
      prev.filter(item => item.timeseries_id !== removedItem.timeseries_id)
    );
    setIsDirty(true);
  };

  const handleClearAllSelectedSoftTags = () => {
    setSelectedSoftTags([]);
    setIsDirty(true);
  };

  const handleConfirmSelectedSoftTags = useCallback(
    async (items: SoftTag[]) => {
      const materialId =
        selectedItem && 'material_code' in selectedItem
          ? selectedItem.id
          : null;
      const routingId =
        selectedItem && 'workcenter' in selectedItem ? selectedItem.id : null;
      const componentId =
        selectedItem && 'component' in selectedItem ? selectedItem.id : null;

      const promises: Promise<unknown>[] = [];

      // Delete tags that were in selectedItem.tags but are no longer in the confirmed list
      const confirmedTimeseriesIds = new Set(
        items.map(item => item.timeseries_id)
      );
      (selectedItem?.tags || []).forEach(tag => {
        if (!confirmedTimeseriesIds.has(tag.timeseries_id)) {
          promises.push(dispatch(removeTagMapping(tag.id)).unwrap());
        }
      });

      // Update existing tags whose priority changed
      const existingTimeseriesIds = new Set(
        (selectedItem?.tags || []).map(t => t.timeseries_id)
      );
      items.forEach(item => {
        const timeseriesId = item.timeseries_id;
        const originalTag = (selectedItem?.tags || []).find(
          t => t.timeseries_id === timeseriesId
        );
        if (!originalTag) {
          return;
        }
        const priorityChanged = originalTag.priority !== item.priority;
        if (priorityChanged) {
          promises.push(
            dispatch(
              updateTagMapping({
                tagMappingId: originalTag.id,
                payload: {
                  priority: item.priority,
                },
              })
            ).unwrap()
          );
        }
      });

      // Create mappings for newly added tags (not already in selectedItem.tags)
      items.forEach(item => {
        const timeseriesId = item.timeseries_id;
        if (existingTimeseriesIds.has(timeseriesId)) {
          return;
        }
        promises.push(
          dispatch(
            createTagMapping(
              mapSoftTagToTagMappingRequest(item, {
                materialId,
                componentId,
                routingId,
              })
            )
          ).unwrap()
        );
      });

      if (promises.length === 0) {
        showNotification('No changes to save.', 'success');
        navigate(-1);
        return;
      }

      const results = await Promise.allSettled(promises);
      const failed = results.filter(
        r => r.status === 'rejected'
      ) as PromiseRejectedResult[];
      const errorMessages = Array.from(
        new Set(
          failed.map(f => getErrorMessage(f.reason).trim()).filter(Boolean)
        )
      );

      if (failed.length === 0) {
        showNotification('Soft tags successfully matched.', 'success');
        navigate(-1);
      } else if (failed.length === results.length) {
        errorMessages.forEach(message => {
          showNotification(message, 'destructive');
        });
      } else {
        showNotification(
          `${results.length - failed.length} of ${results.length} changes saved.`,
          'success'
        );
        errorMessages.forEach(message => {
          showNotification(message, 'destructive');
        });
      }
    },
    [dispatch, selectedItem, showNotification, navigate]
  );

  const onDetailSelect = (softTagId: string) => {
    const found = timeSeriesSoftTags?.assets.find(
      (softTag: AssetSoftTag) => softTag.id === softTagId
    );
    if (found) {
      setSoftTagDetail(mapAssetToSoftTag(found));
    }
  };

  const materialInfo = [
    { label: 'Material No.', value: getItemNumber(selectedItem) },
    { label: 'Material description', value: getItemDescription(selectedItem) },
    {
      label: 'Item family',
      value: getSelectedItemFamily(selectedItem),
    },
    { label: 'Item type', value: getItemTypeFromSelectedItem(selectedItem) },
  ];

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    if (isDirty) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  return (
    <>
      <div className="h-[calc(100vh-172px)] px-6 pt-6 md:px-14 w-full overflow-auto">
        <div className="flex h-full w-full gap-6 lg:gap-8">
          <div className="flex h-full w-[60%] min-h-0 flex-col">
            <div className="mb-4 flex flex-col lg:!flex-row gap-2 lg:!gap-10 rounded-md bg-white px-8 py-4">
              {materialInfo.map(item => (
                <div key={item.label} className="flex flex-col p-2">
                  <Label
                    size="xs"
                    css={{ color: Color.textColorType.neutral.tertiary }}
                  >
                    {item.label}
                  </Label>
                  <Label size="n">{item.value}</Label>
                </div>
              ))}
            </div>
            <div className="flex min-h-0 flex-1 flex-col rounded-md bg-white border border-gray-200">
              <div className="flex flex-col items-start lg:flex-row lg:items-center gap-4 px-6 py-4">
                <Input
                  label="Search by soft tag"
                  size="s"
                  iconLeading="Search"
                  value={searchQuery}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setSearchQuery(e.target.value)
                  }
                />
                <div
                  style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'right',
                    minWidth: '165px',
                    fontWeight: 600,
                    fontSize: '14px',
                  }}
                >
                  <Switch
                    checked={viewOnlyValues}
                    label="View only values"
                    labelPosition="right"
                    onCheckedChange={() => setViewOnlyValues(prev => !prev)}
                  />
                </div>
              </div>
              <Separator
                height="100%"
                mode="light"
                orientation="horizontal"
                width="100%"
              />
              <div className="min-h-0 flex-1 overflow-hidden pb-4">
                <SoftTagList
                  isLoading={isLoading || timeSeriesSoftTagLoading}
                  items={mappedSoftTags}
                  selectedIds={selectedIds}
                  selectedCount={selectedSoftTags.length}
                  maxSelectable={MAX_SELECTED_SOFT_TAGS}
                  onToggleSelect={handleToggleSelect}
                  onDetailSelect={onDetailSelect}
                />
              </div>
            </div>
          </div>
          <div className="h-full w-[40%] min-h-0">
            <SelectedSoftTagList
              items={selectedSoftTags}
              onChange={(list: SoftTag[]) => {
                setSelectedSoftTags(list);
                setIsDirty(true);
              }}
              onRemoveItem={handleRemoveSelectedSoftTag}
              onClearAll={handleClearAllSelectedSoftTags}
              onConfirm={args => {
                setIsDirty(false);
                handleConfirmSelectedSoftTags(args);
              }}
              inUpdateMode={selectedItem?.tags && selectedItem.tags.length > 0}
              tagMappingLoading={tagMappingLoading}
            />
          </div>
        </div>
        <Drawer
          isOpen={!!softTagDetail}
          onClose={handleSoftTagDetailClose}
          title="Soft tag details"
          position="right"
          widthClass="w-[500px]"
          onBack={handleSoftTagDetailClose}
        >
          {!!softTagDetail && (
            <SoftTagDetailCard softTag={softTagDetail as SoftTag} />
          )}
        </Drawer>
      </div>
      <LeaveWithoutConfirmDialog
        open={isLeaveDialogOpen}
        onKeepSelecting={handleKeepSelecting}
        onLeavePage={handleLeavePage}
      />
    </>
  );
};
